// qchat_fe/src/Component/SideData/Overview/AiAgentWorkFlow/triggerAttachment.js
//
// ============================================================
// TRIGGER ATTACHMENT — canonical shape
// ============================================================
//
// A trigger attachment is the {connector_definition_id, field_overrides}
// object stored on a chain node's `post_triggers[*]` or a leadgen form
// node's `triggers[*]`.
//
// This module is the JS mirror of
// `autoBotHandler/src/common_helpers.py::normalise_trigger_attachment`.
// The two must stay in lockstep: the admin produces the shape, the
// backend validates and persists it, and the connector consumes it.
// A change to the shape lands in all three places in one PR.
//
// Shape (canonical, closed):
//   {
//     connector_definition_id: "<uuid>",
//     field_overrides: {
//       "<template_var>": {
//         source:     "{{ field.path }}",     // non-empty
//         convert_to: "string" | "number" | "integer" | "boolean"
//                   | "array" | "json" | "json_array"
//                   | "date" | "time" | "datetime"
//                                             // optional, default "string"
//       },
//       ...
//     }
//   }
//
// Anything else on the object is dropped. `field_overrides` absent and
// `field_overrides: {}` are indistinguishable after normalisation, so a
// caller can treat "no overrides" one way.

export const TRIGGER_CONVERT_TO_ALLOWED = Object.freeze([
  "string",
  "number",
  "integer",
  "boolean",
  "array",
  "json",
  "json_array",
  "date",
  "time",
  "datetime",
]);

const ALLOWED_SET = new Set(TRIGGER_CONVERT_TO_ALLOWED);

/**
 * Normalise one trigger attachment.
 *
 * Returns the canonical object, or null if the input is structurally
 * invalid. Callers on the write path (ChainApis.save, MyQNATest.save)
 * must raise on null; callers on the read path (rendering, comparison)
 * may drop-null silently.
 */
export function normaliseTriggerAttachment(trigger) {
  if (!trigger || typeof trigger !== "object") return null;

  const rawId = trigger.connector_definition_id;
  if (typeof rawId !== "string" || !rawId.trim()) return null;
  const connector_definition_id = rawId.trim();

  const rawOverrides = trigger.field_overrides;
  const field_overrides = {};

  if (rawOverrides && typeof rawOverrides === "object") {
    if (Array.isArray(rawOverrides)) return null;

    for (const [key, entry] of Object.entries(rawOverrides)) {
      if (typeof key !== "string" || !key.trim()) return null;
      if (!entry || typeof entry !== "object") return null;

      const rawSource = entry.source;
      if (typeof rawSource !== "string" || !rawSource.trim()) return null;
      const source = rawSource.trim();

      let convert_to = entry.convert_to;
      if (convert_to !== undefined && convert_to !== null) {
        if (typeof convert_to !== "string") return null;
        convert_to = convert_to.trim().toLowerCase();
        if (!ALLOWED_SET.has(convert_to)) return null;
        // Canonicalise the default away so `{convert_to: "string"}` and
        // `{}` are indistinguishable downstream.
        if (convert_to === "string") convert_to = undefined;
      } else {
        convert_to = undefined;
      }

      const overrideEntry = { source };
      if (convert_to) overrideEntry.convert_to = convert_to;
      field_overrides[key] = overrideEntry;
    }
  } else if (rawOverrides !== undefined && rawOverrides !== null) {
    // Present but not an object — malformed.
    return null;
  }

  const out = { connector_definition_id };
  if (Object.keys(field_overrides).length > 0) {
    out.field_overrides = field_overrides;
  }
  return out;
}

/**
 * Normalise an array of attachments. Invalid entries are dropped, not
 * raised. Use this on read paths (rendering, comparison) where a single
 * malformed entry should not break the whole surface. Write paths use
 * normaliseTriggerAttachment per-entry and raise on null.
 */
export function normaliseTriggerList(triggers) {
  if (!Array.isArray(triggers)) return [];
  const out = [];
  for (const t of triggers) {
    const n = normaliseTriggerAttachment(t);
    if (n) out.push(n);
  }
  return out;
}

/**
 * Build a fresh attachment with no overrides. Used when the operator
 * picks a "Ready" definition — every required field is auto-satisfiable,
 * so no mapping is needed.
 */
export function emptyTriggerAttachment(connectorDefinitionId) {
  if (typeof connectorDefinitionId !== "string" || !connectorDefinitionId.trim()) {
    return null;
  }
  return { connector_definition_id: connectorDefinitionId.trim() };
}

/**
 * Structural equality for two attachments. Used to detect unsaved edits
 * in the mapping modal and to short-circuit redundant writes.
 *
 * Compares the canonical form, so ordering of field_overrides keys is
 * irrelevant and `convert_to: "string"` equals absent convert_to.
 */
export function triggerAttachmentsEqual(a, b) {
  const na = normaliseTriggerAttachment(a);
  const nb = normaliseTriggerAttachment(b);
  if (!na || !nb) return na === nb;

  if (na.connector_definition_id !== nb.connector_definition_id) return false;

  const oa = na.field_overrides || {};
  const ob = nb.field_overrides || {};
  const ka = Object.keys(oa).sort();
  const kb = Object.keys(ob).sort();
  if (ka.length !== kb.length) return false;
  for (let i = 0; i < ka.length; i++) {
    if (ka[i] !== kb[i]) return false;
    const ea = oa[ka[i]];
    const eb = ob[kb[i]];
    if (ea.source !== eb.source) return false;
    if ((ea.convert_to || "") !== (eb.convert_to || "")) return false;
  }
  return true;
}